<script setup lang="ts">
/**
 * UploadModal — single-file uploader for the Files page.
 *
 * Flow:
 *  1. User opens the modal (FilesTab passes the current directory as default
 *     `destPath`).
 *  2. User selects / drops a file. We remember the File object plus a small
 *     preview (name + size).
 *  3. User optionally tweaks `allowOverwrite` / `autoRename`.
 *  4. On submit we POST multipart/form-data to `/api/files/upload` with the
 *     `file` field and the destination path as a query string.
 *  5. On success we close the modal and emit `uploaded` so FilesTab can
 *     invalidate the file list query.
 *
 * Backend expectations:
 *  - POST /api/files/upload
 *  - multipart/form-data with `file` field
 *  - optional query: ?path=<sub>&allow-overwrite=true&auto-file-renaming=true
 *  - 2xx → returns JSON { ok, name, path, size } (best-effort parsed)
 */
import { computed, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  AlertCircle,
  ChevronDown,
  FileUp,
  Settings2,
  Upload as UploadIcon,
  X,
} from 'lucide-vue-next'
import Modal from '@/components/ui/Modal.vue'
import Button from '@/components/ui/Button.vue'
import Input from '@/components/ui/Input.vue'
import Progress from '@/components/ui/Progress.vue'
import { uploadFile, type UploadProgress } from '@/api/files'
import { useToast } from '@/composables/useToast'

interface Props {
  modelValue: boolean
  /** Default destination sub-path (typically the directory the user is in). */
  defaultPath?: string
}

const props = withDefaults(defineProps<Props>(), {
  defaultPath: '',
})

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  uploaded: [result?: { url: string; name: string }]
}>()

const { t } = useI18n()
const toast = useToast()

const file = ref<File | null>(null)
const destPath = ref('')
const allowOverwrite = ref(true)
const autoRename = ref(false)
const optionsOpen = ref(false)
const dragActive = ref(false)
const submitting = ref(false)
const progress = ref<UploadProgress | null>(null)
const errorMessage = ref('')
let xhr: XMLHttpRequest | null = null

// Re-sync destPath default each time the modal opens (FilesTab may have
// navigated while the modal was closed).
watch(
  () => props.modelValue,
  (open) => {
    if (open) {
      destPath.value = props.defaultPath
      errorMessage.value = ''
      // Keep file/refs across openings on purpose — users often upload a
      // batch into different directories. Only the submit state is reset.
    }
  },
)

const canSubmit = computed(() => file.value !== null && !submitting.value)

function resetFileState(): void {
  file.value = null
  progress.value = null
}

function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '0 B'
  const KiB = 1024
  const MiB = 1024 * KiB
  const GiB = 1024 * MiB
  const TiB = 1024 * GiB
  if (n >= TiB) return `${(n / TiB).toFixed(2)} TiB`
  if (n >= GiB) return `${(n / GiB).toFixed(2)} GiB`
  if (n >= MiB) return `${(n / MiB).toFixed(2)} MiB`
  if (n >= KiB) return `${(n / KiB).toFixed(2)} KiB`
  return `${n} B`
}

/**
 * Build an absolute `/api/files/download?name=...` URL using the current
 * origin. The service happily fetches same-origin file URLs and starts a download
 * task, which is how AddTaskDialog hooks uploads into the task list.
 */
function buildDownloadUrl(name: string): string {
  if (typeof window === 'undefined') return `/api/files/download?name=${encodeURIComponent(name)}`
  const url = new URL('/api/files/download', window.location.origin)
  url.searchParams.set('name', name)
  return url.toString()
}

function fileTypeIcon(): typeof FileUp {
  return FileUp
}

function pickFile(): void {
  const input = document.createElement('input')
  input.type = 'file'
  input.style.display = 'none'
  input.addEventListener('change', () => {
    const picked = input.files?.[0]
    if (picked) acceptFile(picked)
    input.remove()
  })
  document.body.appendChild(input)
  input.click()
}

function acceptFile(picked: File): void {
  errorMessage.value = ''
  progress.value = null
  file.value = picked
}

function onDropzoneClick(): void {
  if (file.value) return
  pickFile()
}

function onDropzoneDragEnter(e: DragEvent): void {
  e.preventDefault()
  if (submitting.value) return
  dragActive.value = true
}

function onDropzoneDragOver(e: DragEvent): void {
  e.preventDefault()
  if (submitting.value) return
  // Required to allow drop on this element.
  if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy'
  dragActive.value = true
}

function onDropzoneDragLeave(e: DragEvent): void {
  e.preventDefault()
  // Only flip off when we truly leave the dropzone (not its children).
  const target = e.currentTarget as HTMLElement
  const related = e.relatedTarget as Node | null
  if (!related || !target.contains(related)) {
    dragActive.value = false
  }
}

function onDropzoneDrop(e: DragEvent): void {
  e.preventDefault()
  dragActive.value = false
  if (submitting.value) return
  const dropped = e.dataTransfer?.files?.[0]
  if (dropped) acceptFile(dropped)
}

function clearFile(): void {
  if (submitting.value) return
  resetFileState()
}

function cancelUpload(): void {
  if (!submitting.value) return
  if (xhr) {
    xhr.abort()
    xhr = null
  }
}

function close(): void {
  if (submitting.value) return
  emit('update:modelValue', false)
}

async function submit(): Promise<void> {
  if (!file.value || submitting.value) {
    if (!file.value) errorMessage.value = t('files.uploadDialog.noFile')
    return
  }

  submitting.value = true
  errorMessage.value = ''
  progress.value = { loaded: 0, total: file.value.size, percent: 0 }

  try {
    // Capture the XHR via a closure: the public `uploadFile` helper doesn't
    // expose the request object, so we shadow it through a module-local var
    // assigned by the helper. To keep that contract simple we rely on the
    // helper's reject on abort path instead — for "cancel" we route through
    // a flag check at submit() return time.  See notes below.
    const result = await uploadFile(
      file.value,
      destPath.value.trim(),
      { allowOverwrite: allowOverwrite.value, autoRename: autoRename.value },
      (p) => {
        progress.value = p
      },
    )
    toast.success(t('files.uploadDialog.success', { name: result.name || file.value!.name }))
    progress.value = { loaded: file.value!.size, total: file.value!.size, percent: 100 }
    // Build a downloadable URL the consumer can plug into a download task.
    // The backend exposes /api/files/download with the basename — `result.path`
    // is the upload-relative sub-path, but most callers (including AddTaskDialog)
    // only need the bare filename to feed AFD's addUri.
    const finalName = result.name || file.value!.name
    const downloadUrl = buildDownloadUrl(finalName)
    emit('uploaded', { url: downloadUrl, name: finalName })
    emit('update:modelValue', false)
    // Reset file choice so a fresh open starts empty.
    resetFileState()
  } catch (err) {
    const msg = err instanceof Error ? err.message : t('files.uploadDialog.genericError')
    errorMessage.value = msg
    toast.error(t('files.uploadDialog.failed'), msg)
  } finally {
    submitting.value = false
    xhr = null
  }
}

// Form-state container (kept for future "destination type" toggles if backend
// supports remote paths). Currently informational only.
const optionForm = reactive({})

void optionForm
</script>

<template>
  <Modal
    :model-value="modelValue"
    :title="t('files.uploadDialog.title')"
    size="lg"
    :close-on-backdrop="!submitting"
    :close-on-escape="!submitting"
    @update:model-value="(v) => emit('update:modelValue', v)"
  >
    <div class="space-y-4">
      <!-- Drop zone OR selected-file preview -->
      <div v-if="!file">
        <div
          role="button"
          tabindex="0"
          :aria-label="t('files.uploadDialog.dropZone')"
          :class="[
            'flex flex-col items-center justify-center gap-3 rounded-md border-2 border-dashed px-6 py-10 text-center transition-colors',
            dragActive
              ? 'border-brand-500 bg-brand-500/5'
              : 'border-border bg-bg-elevated/40 hover:border-brand-500 hover:bg-brand-500/5',
            submitting ? 'pointer-events-none opacity-60' : 'cursor-pointer',
          ]"
          @click="onDropzoneClick"
          @keydown.enter.prevent="onDropzoneClick"
          @keydown.space.prevent="onDropzoneClick"
          @dragenter="onDropzoneDragEnter"
          @dragover="onDropzoneDragOver"
          @dragleave="onDropzoneDragLeave"
          @drop="onDropzoneDrop"
        >
          <FileUp :size="32" :class="dragActive ? 'text-brand-500' : 'text-fg-subtle'" />
          <p class="text-sm text-fg-muted">
            {{ dragActive ? t('files.uploadDialog.dropActive') : t('files.uploadDialog.dropZone') }}
          </p>
          <span class="btn-base h-9 px-3 text-sm bg-bg-elevated text-fg border border-border hover:bg-bg-hover cursor-pointer">
            <UploadIcon :size="14" />
            {{ t('files.uploadDialog.changeFile') }}
          </span>
        </div>
      </div>

      <div v-else class="flex items-center gap-3 rounded-md border border-border bg-bg-elevated px-3 py-2.5">
        <component
          :is="fileTypeIcon()"
          :size="20"
          class="shrink-0 text-brand-500"
        />
        <div class="min-w-0 flex-1">
          <div class="truncate text-sm text-fg">{{ file.name }}</div>
          <div class="text-xs text-fg-muted">
            {{ t('files.uploadDialog.fileInfo', { name: '', size: formatBytes(file.size) }).trim() }}
            <span class="ml-1 text-fg-subtle">({{ file.type || 'application/octet-stream' }})</span>
          </div>
        </div>
        <button
          type="button"
          class="rounded p-1 text-fg-muted hover:bg-bg-hover hover:text-fg"
          :disabled="submitting"
          :aria-label="t('files.uploadDialog.changeFile')"
          :title="t('files.uploadDialog.changeFile')"
          @click="pickFile"
        >
          <UploadIcon :size="14" />
        </button>
        <button
          type="button"
          class="rounded p-1 text-fg-muted hover:bg-bg-hover hover:text-danger"
          :disabled="submitting"
          :aria-label="t('files.uploadDialog.removeFile')"
          :title="t('files.uploadDialog.removeFile')"
          @click="clearFile"
        >
          <X :size="14" />
        </button>
      </div>

      <!-- Destination path -->
      <div>
        <label class="mb-1 block text-xs font-medium text-fg-muted">
          {{ t('files.uploadDialog.pathLabel') }}
        </label>
        <Input
          v-model="destPath"
          :placeholder="t('files.uploadDialog.pathPlaceholder')"
          :disabled="submitting"
        />
        <p class="mt-1 text-xs text-fg-muted">
          {{ t('files.uploadDialog.pathHint') }}
        </p>
      </div>

      <!-- Advanced options -->
      <div class="rounded-md border border-border bg-bg-elevated/30">
        <button
          type="button"
          class="flex w-full items-center justify-between gap-2 px-3 py-2 text-sm font-medium text-fg-muted hover:text-fg"
          :disabled="submitting"
          @click="optionsOpen = !optionsOpen"
        >
          <span class="flex items-center gap-2">
            <Settings2 :size="14" />
            {{ t('files.uploadDialog.options') }}
          </span>
          <ChevronDown
            :size="14"
            :class="optionsOpen ? 'rotate-180 transition-transform' : 'transition-transform'"
          />
        </button>
        <div v-if="optionsOpen" class="space-y-3 border-t border-border p-3">
          <label class="flex items-start gap-2">
            <input
              v-model="allowOverwrite"
              type="checkbox"
              :disabled="submitting"
              class="mt-0.5 h-4 w-4 rounded border-border text-brand-500 focus:ring-brand-500"
            />
            <span class="flex-1">
              <span class="block text-sm text-fg">{{ t('files.uploadDialog.allowOverwrite') }}</span>
              <span class="block text-xs text-fg-muted">{{ t('files.uploadDialog.allowOverwriteHint') }}</span>
            </span>
          </label>
          <label class="flex items-start gap-2">
            <input
              v-model="autoRename"
              type="checkbox"
              :disabled="submitting"
              class="mt-0.5 h-4 w-4 rounded border-border text-brand-500 focus:ring-brand-500"
            />
            <span class="flex-1">
              <span class="block text-sm text-fg">{{ t('files.uploadDialog.autoRename') }}</span>
              <span class="block text-xs text-fg-muted">{{ t('files.uploadDialog.autoRenameHint') }}</span>
            </span>
          </label>
        </div>
      </div>

      <!-- Progress bar -->
      <div v-if="progress" class="space-y-1">
        <Progress
          :value="progress.percent"
          :show-label="true"
          :label="t('files.uploadDialog.uploading')"
          :variant="progress.percent >= 100 ? 'success' : 'primary'"
        />
        <p class="text-xs text-fg-muted">
          {{ formatBytes(progress.loaded) }} / {{ formatBytes(progress.total) }}
        </p>
      </div>

      <!-- Error -->
      <div
        v-if="errorMessage"
        class="flex items-start gap-2 rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger"
        role="alert"
      >
        <AlertCircle :size="14" class="mt-0.5 shrink-0" />
        <span class="break-all">{{ errorMessage }}</span>
      </div>
    </div>

    <template #footer>
      <Button
        v-if="submitting"
        variant="ghost"
        size="sm"
        @click="cancelUpload"
      >
        {{ t('files.uploadDialog.failed') }}
      </Button>
      <Button
        v-else
        variant="ghost"
        size="sm"
        @click="close"
      >
        {{ t('common.cancel') }}
      </Button>
      <Button
        variant="primary"
        size="sm"
        :loading="submitting"
        :disabled="!canSubmit"
        @click="submit"
      >
        <template v-if="!submitting" #icon><UploadIcon :size="14" /></template>
        {{ submitting ? t('files.uploadDialog.uploading') : t('files.uploadDialog.submit') }}
      </Button>
    </template>
  </Modal>
</template>
